import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, useTheme } from '@mui/styles';
import { useNavigate } from 'react-router-dom';
import ReactHtmlParser from 'html-react-parser';

import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';

import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AddIcon from '@mui/icons-material/Add';

import { get as _get, debounce as _debounce } from 'lodash';
import {
    scaleLinear as D3scaleLinear,
    scaleOrdinal as D3scaleOrdinal,
    format as D3format,
} from 'd3';
import Moment from 'moment';

import { GetISODateFromQuick } from '../../../../libraries/helpers';
import Validator, { useValidate, checkValidity } from '../../../../components/validator';
import DateRange from './../components/date-range';
import SparklineGrid, { CalcVisibleSeries } from './../components/responsive-sparkline-grid';
import SourceSelector from './../components/source-selector';
import FilterBuilder, { EMPTY_FILTER } from '../../../../components/filter/filterbuilder';
import PopupMenu from '../../../../components/popupmenu';

import { GroupsContext } from '../../../../data/groups-service';
import DashboardService from '../../../../data/dashboard-service';
import MetadataService from '../../../../data/metadata-service';
import { ParamsSerializer } from '../../../../data/auth-service';
import { VerifyDates } from '../../widget';

const useStyles = makeStyles(theme => ({
    root: {
    },
    label: {
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        width: '100%',
        justifyContent: 'left',
        marginTop: 20,
        textAlign: 'left',
    },
    bar: {
        display: 'inline-block',
        marginTop: 0,
        height: 7,
        position: 'relative',
        top: -7,
    },
    bars: {
        listStyleType: 'none',
        paddingLeft: '0',
        paddingTop: 14,
    },
    // editor classes
    editorRoot: {
        paddingTop: theme.spacing(1),
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
    },
    editorFooter: {
        alignSelf: 'flex-end',
        width: '100%',
    },
    editorMargin: {
        margin: theme.spacing(2),
    },
    inlineTag: {
        display: 'inline-block',
        verticalAlign: 'middle',
        padding: 5,
    },
    tagsButton: {
        width: 99,
    },
    filterText: {
        marginRight: 20,
        overflow: 'hidden',
        whiteSpace: 'break-spaces',
    },
    deleteButton: {
        paddingRight: '20px !important',
        '& button': {
            float: 'right',
        },
    },
    inlineForm: {
        margin: theme.spacing(1),
        '& .MuiTextField-root': {
            margin: theme.spacing(1),
        },
    },
    inlineChips: {
        display: 'inline-block',
        verticalAlign: 'middle',
        padding: 5,
    },
    series: {
        backgroundColor: theme.palette.background.paper,
    },
}));

const DEFAULTS = edit => edit
    ? { minW: 12, minH: 12 }
    : { minW: 5, minH: 5 };

// don't display until size-me can figure out what our size is
const MtdtTimelineWidget = (props) => {
    return !props.size || !props.size.width ? ''
        : (
            props.data.edit
                ? <Editor { ...props }/>
                : <Widget { ...props } />
        );
};
MtdtTimelineWidget.propTypes = {
    data: PropTypes.object.isRequired,
    size: PropTypes.object,
};

const Widget = (props) => {
    const classes = useStyles();
    const theme = useTheme();

    const { groupKeys } = useContext(GroupsContext);
    const { cmcs } = useContext(GroupsContext);
    const [data, setData] = useState([{ metrics: [], timeline: [] }]);
    const [max, setMax] = useState(() => 0);
    const [height, setHeight] = useState(0);
    const navigate = useNavigate();
    const [, setErrors] = useState(0);
    const [limit, setLimit] = useState(0);
    const [start, setStart] = useState('');
    const [end, setEnd] = useState();

    const [settings] = useState({
        margin: 0,
        labelWidth: 0,
        chartHeight: 40,
        scale: 'linear',
        vgrid: true,
        hgrid: true,
    });

    const barScale = () => D3scaleLinear()
        .range([0, 60])
        .domain([0, max || 0]);

    const fetch = controller => {
        props.setBusy(true, false);
        const start = props.data.quick ? GetISODateFromQuick(props.data.quick) : props.data.start;
        const end = props.data.quick ? undefined : props.data.end;
        setStart(start);
        setEnd(end);
        return DashboardService.getMtdtTimelineSeries({
            start,
            end,
            filter: props.data.settings.json_filter,
            targets: props.data.settings.targets,
            source: props.data.settings.source || ['conn'],
            limit,
        }, controller).catch(err => {
            props.onError(err, props.data.title);
            setErrors(current => {
                props.setBusy(false, current > 2);
                if (current > 2) DashboardService.unsubscribe(props.data.id);
                return current + 1;
            });
            return 'error';
        });
    };

    const colors = D3scaleOrdinal()
        .range(theme.SeverityColors.concat(theme.ChartColors))
        .domain(groupKeys);

    const callback = res => {
        if (res === 'error') return;
        setErrors(0);
        props.setBusy(false);

        const smap = {};
        (_get(res, 'timeline', []) || []).forEach(d => {
            d.timestamp = d.key_as_string;
            Object.entries(d.series || {}).forEach(([k, v], i) => {
                smap[k] = {
                    count: _get(smap, `${k}.count`, 0) + v,
                    key: k,
                    title: k,
                    value: d => _get(d, `series.${k}`, 0),
                    id: k, // cmc name
                };
            });
        });

        const series = Object.values(smap);

        const max = series.reduce((a, c) => Math.max(a, c.count), 0);
        const da = [{
            index: 0,
            key: 'mtdt timelines',
            // [{key, count}]
            metrics: series.sort((a, b) => b.count - a.count),
            // [{doc_count, series: {key: count}}]
            timeline: (_get(res, 'timeline', []) || []).sort((a, b) =>
                new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()),
        }];
        setHeight(da[0].metrics.length * settings.chartHeight);
        setData(da);
        setMax(max);
    };

    const subscribe = () => {
        setErrors(0);
        // truncate the data if we need to
        if (data[0].metrics.length > limit) {
            const d = data[0];
            d.metrics = d.metrics.slice(0, limit);
            setData([d]);
            setHeight(d.metrics.length * settings.chartHeight);
        }
        DashboardService.subscribe(props.data.id, fetch, callback);
    };

    const handleGroupClick = sid => {
        if (data.length < 1) return;

        const s = (data[0].metrics || []).find(s => s.id === sid);
        if (!s) return;
        const cmc = (cmcs || []).find(c => c.name === sid);
        if (!cmc) return;

        const p = {
            start,
            end,
            quick: props.data.quick,
            sources: s.types,
            filter: JSON.stringify(s.json_filter || EMPTY_FILTER),
            cmc: cmc.uuid,
        };
        navigate(`/views/metadata?${ParamsSerializer(p)}`);
    };

    const handleSparklineClick = useCallback((d, k, sid) => {
        if (data.length < 1) return;

        console.log(data[0].metrics);
        const s = (data[0].metrics || []).find(s => s.id === k);
        if (!s) return;
        const cmc = (cmcs || []).find(c => c.name === k);
        if (!cmc) return;

        const p = {
            start: d.timestamp,
            end: Moment(d.timestamp).add(10, 'm').utc().toISOString(),
            sources: s.types,
            filter: s.json_filter && s.json_filter.nodes
                ? JSON.stringify(s.json_filter)
                : JSON.stringify(EMPTY_FILTER),
            cmc: cmc.uuid,
        };
        navigate(`/views/metadata?${ParamsSerializer(p)}`);
    }, [navigate, cmcs, data]);

    // we only need one debouncer
    const setLimitDebounce = useMemo(() => _debounce(setLimit, 420), []);

    // incoming
    useEffect(() => {
        setErrors(0);
        if (!_get(props.size, 'height', 0)) return;
        setLimitDebounce(Math.min(CalcVisibleSeries(props.size.height, 60, settings.chartHeight), 42));
    }, [props.size, settings.chartHeight, setLimitDebounce]);

    useEffect(() => {
        setErrors(0);
        return () => {
            // unsubscribe to data feed while editing (widget control exit)
            // will also force a refresh when resubscribing after edit is done
            DashboardService.unsubscribe(props.data.id);
        };
    }, [props.data.id]);

    useEffect(() => {
        if (!limit) return;
        subscribe();
        setStart(props.data.quick ? GetISODateFromQuick(props.data.quick) : props.data.start);
        setEnd(props.data.quick ? undefined : props.data.end);
        return () => {
            // unsubscribe to data feed while editing (widget control exit)
            // will also force a refresh when resubscribing after edit is done
            DashboardService.unsubscribe(props.data.id);
        };
    }, [props.data.id, limit, props.data.start, props.data.end]);

    return (
        <div
            className={classes.root}
            style={{ width: props.size.width, height: props.size.height }}>
            <Grid container
                spacing={2}
                direction="row"
                wrap='nowrap'
                justifyContent="space-between"
                style={{ width: 'calc(100% - 20px)' }}>

                <Grid item style={{ maxWidth: 150 }}>
                    <div style={{ marginTop: 20 }}>
                        {data[0].metrics.map((m, i) => (
                            <Tooltip arrow placement="top"
                                key={`${m.title}_${i}`}
                                title={m.title}>
                                <Link className={classes.label}
                                    aria-label={m.title}
                                    component="button"
                                    onClick={() => handleGroupClick(m.id)}
                                    variant="body2">
                                    {m.title}
                                </Link>
                            </Tooltip>
                        ))}
                    </div>
                </Grid>

                <SparklineGrid
                    metrics={data}
                    height={height}
                    width={props.size.width - 250}
                    start={start}
                    end={end}
                    settings={settings}
                    onClick={handleSparklineClick}
                    colors={colors}
                    key={props.data.id}>
                </SparklineGrid>

                <Grid style={{ maxWidth: 100 }}>
                    <div style={{ marginTop: 20, maxWidth: 100 }}>
                        <ul className={classes.bars}>
                            {data[0].metrics.map((m, i) => (
                                <li key={`${m.key}_${i}`} style={{ paddingBottom: 0 }}>
                                    <div>
                                        <div>{D3format(',')(m.count)}</div>
                                        <div className={classes.bar}
                                            style={{
                                                width: barScale()(m.count) || 5,
                                                backgroundColor: colors(m.key),
                                            }}>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </Grid>

            </Grid>
        </div>
    );
};

Widget.propTypes = {
    data: PropTypes.object.isRequired,
    size: PropTypes.object,
    hover: PropTypes.bool,
    setBusy: PropTypes.func,
    onError: PropTypes.func,
};

const Editor = (props) => {
    const classes = useStyles();

    const { cmcs } = useContext(GroupsContext);

    const [title, setTitle] = useState(props.data.title);
    const [filter, setFilter] = useState(props.data.settings.json_filter);
    const [fields, setFields] = useState([]);
    const [targets, setTargets] = useState(props.data.settings.targets || []);
    const [source, setSource] = useState(props.data.settings.source || ['conn']);
    const [showFilter, setShowFilter] = useState(false);
    const [range, setRange] = useState({
        start: props.data.start,
        end: props.data.end,
        quick: props.data.quick,
    });

    const handleRangeChange = range => {
        setRange({ ...range });
    };

    const handleTitleChange = (e, validID, valid) => {
        setTitle(e.target.value);
    };

    const handleFilterChange = ({ filter }) => {
        setFilter(filter);
        setShowFilter(false);
    };

    const handleSaveChanges = () => {
        props.onOK({
            ...props.data,
            ...range,
            title,
            settings: {
                ...props.data.settings,
                json_filter: filter,
                targets,
                source,
            },
        });
    };

    const handleRemoveTarget = t => {
        const i = targets.findIndex(tt => t === tt);
        if (i >= 0) targets.splice(i, 1);
        setTargets([...targets]);
    };

    const handleAddTarget = t => {
        const i = targets.findIndex(tt => t.name === tt);
        if (i < 0) targets.push(t.name);
        setTargets([...targets]);
    };

    const handleRemoveSource = s => {
        const i = source.findIndex(src => s === src);
        if (i >= 0) source.splice(i, 1);
        setSource([...source]);
    };

    const handleAddSource = s => {
        const i = source.findIndex(src => s === src);
        if (i < 0) source.push(s);
        setSource([...source]);
    };

    const fetchFields = async () => {
        const fields = await MetadataService.cacheFields();
        setFields(fields.filter(c => c.indexed && c.type !== 'timestamp' && c.type !== 'date'));
    };

    const isValid = () => {
        if (!VerifyDates({ ...range, override: props.data.date_override })) return false;
        if ((source || []).length < 1) return false;
        return true;
    };

    // initial hook
    useEffect(() => {
        fetchFields();
    }, []);

    return (
        <Box
            display="flex" flexWrap="wrap"
            className={classes.editorRoot}
            style={{ width: props.size.width - 5, height: props.size.height - 20 }}>
            <Box>
                <div className={classes.inlineForm}>
                    <Validator
                        style={{ width: 200 }}
                        label="Title"
                        aria-label="Title"
                        value={title}
                        onChange={handleTitleChange}/>
                    {props.data.date_override &&
                        <DateRange
                            onChange={handleRangeChange}
                            schedule={range} />
                    }
                </div>
                <div className={classes.editorMargin}>
                    <Grid container spacing={1} alignItems="flex-end">
                        <Grid item>
                            <SourceSelector
                                onAdd={handleAddSource}
                                onRemove={handleRemoveSource}
                                sources={source}
                                button={
                                    <Button
                                        aria-label="add source"
                                        title="add source"
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        startIcon={<AddIcon/>}>Source
                                    </Button>
                                }/>
                        </Grid>
                    </Grid>
                </div>
                <div className={classes.editorMargin}>
                    <Grid container spacing={1} alignItems="flex-end">
                        <Grid item>
                            <FilterBuilder
                                onOK={handleFilterChange}
                                onShow={setShowFilter}
                                show={showFilter}
                                fields={fields}
                                filter={filter}
                                placement="right-start"
                                button={
                                    <Button
                                        aria-label="filter"
                                        title="filter"
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        startIcon={<AccountTreeIcon/>}>Filter
                                    </Button>
                                }/>
                            <Typography
                                variant="body2"
                                component="span"
                                className={classes.filterText}>
                                {ReactHtmlParser(MetadataService.toEnglish(filter, true))}
                            </Typography>
                        </Grid>
                    </Grid>
                </div>
                <div className={classes.editorMargin}>
                    <Grid container spacing={1} alignItems="flex-end">
                        <Grid item>
                            <PopupMenu
                                onSelect={handleAddTarget}
                                aria-label="Filter by cmc(s)"
                                title="Filter by cmc(s)"
                                items={cmcs}
                                itemkey="uuid"
                                itemTitle="name"
                                button={
                                    <Button
                                        aria-label="filter"
                                        title="filter"
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        startIcon={<AddIcon/>}>CMC
                                    </Button>
                                }/>
                            {targets.map((t, i) =>
                                <div key={`${t}_i`} className={classes.inlineChips}>
                                    <Chip
                                        color="default"
                                        label={t}
                                        onDelete={() => handleRemoveTarget(t)}
                                        className={classes.chip}/>
                                </div>
                            )}
                        </Grid>
                    </Grid>
                </div>
            </Box>
            <Box className={classes.editorFooter}>
                <DialogActions style={{ paddingBottom: 0 }}>
                    <Button
                        aria-label="ok"
                        onClick={handleSaveChanges}
                        color="primary"
                        disabled={!isValid()}>
                    OK
                    </Button>
                    <Button aria-label="cancel" onClick={props.onCancel} color="primary" autoFocus>
                    Cancel
                    </Button>
                </DialogActions>
            </Box>
        </Box>
    );
};
Editor.propTypes = {
    data: PropTypes.object.isRequired,
    size: PropTypes.object,
    onOK: PropTypes.func,
    onCancel: PropTypes.func,
};

export {
    MtdtTimelineWidget as default,
    DEFAULTS,
};
